import { accessToken, login } from '@/auth/oidc'
import { ApiError, request } from '@/lib/api'
import { saveBlob } from '@/lists/api'
import type {
  AgeingLine,
  Dashboard,
  DashboardWidget,
  LoadedDashboard,
  LoadedWidget,
  SaveDashboardRequest,
  SaveWidgetRequest,
  WidgetPlacement,
  CashFlowForecastLine,
  CashFlowLine,
  DealCycle,
  DeadStockRow,
  InventoryClassification,
  LossReason,
  PickerPerformance,
  PnlDimension,
  PnlReport,
  Period,
  ReportDefinition,
  ReportDelivery,
  ReportResult,
  ReportRun,
  ReportSchedule,
  ReportSource,
  SavedReport,
  SaveReportRequest,
  SaveScheduleRequest,
  StageConversion,
  SupplierQuality,
  UnitEconomicsChannel,
} from './types'

/**
 * Клиент API аналитики (ТЗ §4.6) — тикет #76.
 *
 * Никакой логики отчётов здесь нет и быть не должно: формулы конверсии, ABC и CAC живут
 * на сервере, а белый список витрин — в `ReportCatalog`. Клиент показывает то, что ему
 * отдали, и просит то, что ему разрешили назвать.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

// ------------------------------------------------------------------ конструктор

/** Витрины, доступные текущему пользователю. Список уже сужен его правами на сервере. */
export function fetchSources(signal?: AbortSignal): Promise<ReportSource[]> {
  return request<ReportSource[]>('/analytics/sources', { signal })
}

/**
 * Предпросмотр несохранённого определения.
 *
 * Право на источник проверяется здесь так же, как при запуске сохранённого отчёта: иначе
 * предпросмотр стал бы обходным путём к тем же данным.
 */
export function previewReport(
  definition: ReportDefinition,
  limit = 50,
  signal?: AbortSignal,
): Promise<ReportResult> {
  return request<ReportResult>(`/analytics/preview?limit=${limit}`, {
    method: 'POST',
    body: definition,
    signal,
  })
}

export function fetchReports(signal?: AbortSignal): Promise<SavedReport[]> {
  return request<SavedReport[]>('/analytics/reports', { signal })
}

export function createReport(body: SaveReportRequest): Promise<SavedReport> {
  return request<SavedReport>('/analytics/reports', { method: 'POST', body })
}

export function updateReport(reportId: string, body: SaveReportRequest): Promise<SavedReport> {
  return request<SavedReport>(`/analytics/reports/${reportId}`, { method: 'PUT', body })
}

export function deleteReport(reportId: string): Promise<void> {
  return request<void>(`/analytics/reports/${reportId}`, { method: 'DELETE' })
}

/**
 * Запускает сохранённый отчёт.
 *
 * Право на источник проверяется сервером **от имени запускающего**, а не того, кто отчёт
 * сохранил. Отказ приходит как 422 с кодом `report.forbidden_source` — и это отказ, а не
 * пустой список: пустота читалась бы как «данных нет».
 */
export function runReport(reportId: string, signal?: AbortSignal): Promise<ReportResult> {
  return request<ReportResult>(`/analytics/reports/${reportId}/run`, {
    method: 'POST',
    signal,
  })
}

export function fetchRuns(reportId: string, signal?: AbortSignal): Promise<ReportRun[]> {
  return request<ReportRun[]>(`/analytics/reports/${reportId}/runs`, { signal })
}

/**
 * Выгружает отчёт файлом.
 *
 * Мимо `request`, потому что ответ — не JSON, а байты. Обработка 401 и ошибок повторена
 * дословно: расхождение здесь означало бы, что истёкшая сессия на выгрузке ведёт себя
 * иначе, чем везде.
 */
export async function exportReport(
  reportId: string,
  fileName: string,
  format: 'csv' | 'xlsx' = 'csv',
  columnHeaders: Record<string, string> = {},
): Promise<void> {
  const token = await accessToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  // Подписи колонок — те, что на экране (#88): словарь живёт здесь, а не на сервере.
  const response = await fetch(
    `${BASE_URL}/analytics/reports/${reportId}/export?format=${format}`,
    { method: 'POST', headers, body: JSON.stringify({ headers: columnHeaders }) },
  )

  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      problem?.code ?? null,
      problem?.detail ?? problem?.message ?? response.statusText,
    )
  }
  saveBlob(await response.blob(), `${fileName}.${format}`)
}

// ------------------------------------------------------------------ расписания

export function fetchSchedules(signal?: AbortSignal): Promise<ReportSchedule[]> {
  return request<ReportSchedule[]>('/analytics/schedules', { signal })
}

export function createSchedule(body: SaveScheduleRequest): Promise<ReportSchedule> {
  return request<ReportSchedule>('/analytics/schedules', { method: 'POST', body })
}

export function updateSchedule(
  scheduleId: string,
  body: SaveScheduleRequest,
): Promise<ReportSchedule> {
  return request<ReportSchedule>(`/analytics/schedules/${scheduleId}`, { method: 'PUT', body })
}

export function deleteSchedule(scheduleId: string): Promise<void> {
  return request<void>(`/analytics/schedules/${scheduleId}`, { method: 'DELETE' })
}

/** Последние письма расписания (#139). */
export function fetchScheduleDeliveries(scheduleId: string, signal?: AbortSignal): Promise<ReportDelivery[]> {
  return request<ReportDelivery[]>(`/analytics/schedules/${scheduleId}/deliveries?limit=20`, { signal })
}

// ------------------------------------------------------------------ готовые отчёты

function period({ from, to }: Period): string {
  return `from=${from}&to=${to}`
}

export function fetchFunnelConversion(
  funnelId: string,
  range: Period,
  signal?: AbortSignal,
): Promise<StageConversion[]> {
  return request(`/analytics/funnel/${funnelId}/conversion?${period(range)}`, { signal })
}

export function fetchFunnelCycle(
  funnelId: string,
  range: Period,
  signal?: AbortSignal,
): Promise<DealCycle[]> {
  return request(`/analytics/funnel/${funnelId}/cycle?${period(range)}`, { signal })
}

export function fetchLossReasons(
  funnelId: string,
  range: Period,
  signal?: AbortSignal,
): Promise<LossReason[]> {
  return request(`/analytics/funnel/${funnelId}/loss-reasons?${period(range)}`, { signal })
}

export function fetchPnl(
  dimension: PnlDimension,
  range: Period,
  signal?: AbortSignal,
): Promise<PnlReport> {
  return request(`/analytics/pnl?dimension=${dimension}&${period(range)}`, { signal })
}

export function fetchCashFlow(
  range: Period,
  signal?: AbortSignal,
  organizationId?: string | null,
): Promise<CashFlowLine[]> {
  return request(`/analytics/cash-flow?${period(range)}${organization(organizationId)}`, { signal })
}

/**
 * Организация отчёта ДДС. Сервер без неё отказывает 422 `cash_flow.organization_required`:
 * деньги разных юрлиц не складываются. Экран спрашивает её до запроса.
 */
function organization(organizationId?: string | null): string {
  return organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : ''
}

export function fetchCashFlowForecast(
  range: Period,
  signal?: AbortSignal,
  organizationId?: string | null,
): Promise<CashFlowForecastLine[]> {
  return request(`/analytics/cash-flow/forecast?${period(range)}${organization(organizationId)}`, { signal })
}

export function fetchReceivables(signal?: AbortSignal): Promise<AgeingLine[]> {
  return request('/analytics/receivables', { signal })
}

export function fetchUnitEconomics(
  range: Period,
  signal?: AbortSignal,
): Promise<UnitEconomicsChannel[]> {
  return request(`/analytics/unit-economics?${period(range)}`, { signal })
}

export function fetchAbcXyz(
  range: Period,
  warehouseId?: string | null,
  signal?: AbortSignal,
): Promise<InventoryClassification[]> {
  const warehouse = warehouseId ? `&warehouseId=${warehouseId}` : ''
  return request(`/analytics/inventory/abc-xyz?${period(range)}${warehouse}`, { signal })
}

export function fetchDeadStock(
  notMovedSince: string,
  limit = 100,
  signal?: AbortSignal,
): Promise<DeadStockRow[]> {
  return request(
    `/analytics/inventory/dead-stock?notMovedSince=${notMovedSince}&limit=${limit}`,
    { signal },
  )
}

export function fetchPickerPerformance(
  range: Period,
  signal?: AbortSignal,
): Promise<PickerPerformance[]> {
  return request(`/analytics/warehouse/picker-performance?${period(range)}`, { signal })
}

export function fetchSupplierQuality(
  range: Period,
  signal?: AbortSignal,
): Promise<SupplierQuality[]> {
  return request(`/analytics/warehouse/supplier-quality?${period(range)}`, { signal })
}

// ------------------------------------------------------------------ дашборды (#77)

/**
 * Дашборды, доступные пользователю: личные и общие для его ролей.
 *
 * Список уже сужен сервером. Данных виджетов здесь нет — только состав: исполнять отчёты
 * ради списка значило бы прогнать их все, чтобы показать имена.
 */
export function fetchDashboards(signal?: AbortSignal): Promise<Dashboard[]> {
  return request<Dashboard[]>('/analytics/dashboards', { signal })
}

/**
 * Дашборд, который показывается при входе.
 *
 * `null`, если его нет: сервер отвечает 204, и это штатное состояние нового
 * пользователя, а не ошибка. Пустой ответ `request` отдаёт как `null`.
 */
export async function fetchDefaultDashboard(signal?: AbortSignal): Promise<Dashboard | null> {
  return (await request<Dashboard | null>('/analytics/dashboards/default', { signal })) ?? null
}

/**
 * Дашборд с виджетами.
 *
 * Каждый виджет исполняется сервером **от имени смотрящего**. Виджет поверх закрытого
 * источника приходит с `errorCode` и без `rows` — отказ, а не пустая таблица.
 */
export function fetchDashboard(
  dashboardId: string,
  withData = true,
  signal?: AbortSignal,
): Promise<LoadedDashboard> {
  return request<LoadedDashboard>(
    `/analytics/dashboards/${dashboardId}?withData=${withData}`,
    { signal },
  )
}

/** Перечитывает один виджет — для автообновления. */
export function refreshWidget(
  dashboardId: string,
  widgetId: string,
  signal?: AbortSignal,
): Promise<LoadedWidget> {
  return request<LoadedWidget>(
    `/analytics/dashboards/${dashboardId}/widgets/${widgetId}/refresh`,
    { method: 'POST', signal },
  )
}

export function createDashboard(body: SaveDashboardRequest): Promise<Dashboard> {
  return request<Dashboard>('/analytics/dashboards', { method: 'POST', body })
}

export function updateDashboard(
  dashboardId: string,
  body: SaveDashboardRequest,
): Promise<Dashboard> {
  return request<Dashboard>(`/analytics/dashboards/${dashboardId}`, { method: 'PUT', body })
}

export function deleteDashboard(dashboardId: string): Promise<void> {
  return request<void>(`/analytics/dashboards/${dashboardId}`, { method: 'DELETE' })
}

export function addWidget(
  dashboardId: string,
  body: SaveWidgetRequest,
): Promise<DashboardWidget> {
  return request<DashboardWidget>(`/analytics/dashboards/${dashboardId}/widgets`, {
    method: 'POST',
    body,
  })
}

export function updateWidget(
  dashboardId: string,
  widgetId: string,
  body: SaveWidgetRequest,
): Promise<DashboardWidget> {
  return request<DashboardWidget>(
    `/analytics/dashboards/${dashboardId}/widgets/${widgetId}`,
    { method: 'PUT', body },
  )
}

export function deleteWidget(dashboardId: string, widgetId: string): Promise<void> {
  return request<void>(`/analytics/dashboards/${dashboardId}/widgets/${widgetId}`, {
    method: 'DELETE',
  })
}

/**
 * Сохраняет раскладку целиком.
 *
 * Одним запросом на все виджеты: перетаскивание сдвигает соседей, и раскладка,
 * сохранённая наполовину, — это виджеты друг на друге.
 */
export function saveLayout(
  dashboardId: string,
  placements: WidgetPlacement[],
): Promise<DashboardWidget[]> {
  return request<DashboardWidget[]>(`/analytics/dashboards/${dashboardId}/layout`, {
    method: 'PUT',
    body: placements,
  })
}
